import localforage from 'localforage';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { useDeficiencyFiles } from '../../rxdb/deficiencyFiles/useDeficiencyFiles';
import { DEBUG } from '../../rxdb/handleReplicationErrors';
import { useInspectionFiles } from '../../rxdb/inspectionFiles/useInspectionFiles';
import type { InspectionListDocument } from '../../rxdb/inspectionList/inspectionList.types';
import { useInspectionList } from '../../rxdb/inspectionList/useInspectionList';
import { LiveInterval } from '../../rxdb/LiveInterval';
import type { SignAndStampFileDocument } from '../../rxdb/signAndStamp/signAndStamp.types';
import { useSignAndStampFile } from '../../rxdb/signAndStamp/useSignAndStampFile';
import type { SignAndStampUserLevelDocument } from '../../rxdb/signAndStampUserLevel/signAndStampUserLevel.types';
import { useSignAndStampUserLevel } from '../../rxdb/signAndStampUserLevel/useSignAndStampUserLevel';
import type {
  WorkPackageFiles,
  WorkPackageFilesDocument,
} from '../../rxdb/workPackageFiles/workPackageFiles.types';
import { useWorkPackageFiles } from '../../rxdb/workPackageFiles/useWorkPackageFiles';
import { WorkPackagesDocument } from '../../rxdb/workPackages/workPackages.types';
import { useWorkPackages } from '../../rxdb/workPackages/useWorkPackages';
import { isJestTest } from '../../test-helpers/isJestTest';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { ApiStatus } from '../../utilities/enums';
import { useFeatureFlags } from '../../utilities/useFeatureFlag';
import type { FeatureFlags } from '../../types/featureFlags';
import {
  checkIfExist,
  disciplineMapping,
  getValueFromLocalStorage,
  setDeltaFileData,
} from '../../utilities/utilityFunctions';
import { WorkPackageFilesOnline } from '../model/model';

const attachmentStore = localforage.createInstance({
  name: 'attachment-store',
});
const store = localforage.createInstance({
  name: 'imageUploadQueue',
  storeName: 'imageUploadQueue',
});

const CacheUpdateHandler = () => {
  const online = useOnlineStatus();
  const {
    inspectionFilesCount,
    primaryContext,
    SiteDiscipline,
    deficiencyFileCount,
    filesCount,
    syncData,
    HandleGesture,
  } = useMst();
  const featureFlag: FeatureFlags = useFeatureFlags();
  const { uploadFilesInCache, getImage } = useFileStorage();
  const [workPackageFiles, setWorkPackageFiles] = useState<WorkPackageFilesDocument[]>([]);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();
  const [signAndStampUserLevelUpdateSubscription, setSignAndStampUserLevelUpdateSubscription] =
    useState<Subscription>();
  const [signAndStampFileUpdateSubscription, setSignAndStampFileUpdateSubscription] =
    useState<Subscription>();
  const [wpSubscription, setWpSubscription] = useState<Subscription>();
  const [inspectionListSubscription, setInspectionListSubscription] = useState<Subscription>();
  const [workPackageData, setWorkPackageData] = useState<WorkPackagesDocument[]>([]);
  const [inspectionListData, setInspectionListData] = useState<InspectionListDocument[]>([]);
  const { workPackagesInitialized, workPackagesCollection, getWorkPackages } = useWorkPackages();
  const { inspectionListInitialized, inspectionListCollection, getAllInspectionList } =
    useInspectionList();
  const { workPackageFilesInitialized, workPackageFilesCollection } = useWorkPackageFiles();
  const { inspectionFilesInitialized, inspectionFilesCollection } = useInspectionFiles();
  const { deficiencyFilesInitialized, deficiencyFilesCollection } = useDeficiencyFiles();
  const { signAndStampFileInitialized, signAndStampFileCollection, getSignAndStampFiles } =
    useSignAndStampFile();
  const {
    signAndStampUserLevelInitialized,
    signAndStampUserLevelCollection,
    getSignAndStampUserLevel,
  } = useSignAndStampUserLevel();
  const MAX_RETRIES = 5;
  const BATCH_SIZE = featureFlag.fileDownloadBatchSize
    ? Number(featureFlag.fileDownloadBatchSize)
    : 250;
  const [downloadedFiles, setDownloadedFiles] = useState<{ fileId: string; retryCount: number }[]>(
    () => {
      const saved = localStorage.getItem('downloadedFiles');
      return saved ? JSON.parse(saved) : [];
    },
  );

  const getKeysFromAttachmentStore = () => {
    const val = attachmentStore
      .keys()
      .then(function (keys) {
        return keys;
      })
      .catch(function (error) {
        console.log(error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: CacheUpdateHandler.tsx ~ line 118 ~ getKeysFromAttachmentStore ~',
        );
      });
    return val;
  };

  const getKeysFromUploadQueue = () => {
    const val = store
      .keys()
      .then(function (keys) {
        return keys;
      })
      .catch(function (error) {
        console.log(error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: CacheUpdateHandler.tsx ~ line 135 ~ getKeysFromUploadQueue ~',
        );
      });
    return val;
  };

  const downloadFromS3 = async (filePath: string) => {
    if (isJestTest()) return '';
    try {
      return await getImage(filePath).then(val => {
        return fetch(String(val))
          .then(r => r.blob())
          .then(e => {
            return e;
          });
      });
    } catch (error) {
      console.log('Flow: downloadFromS3 --->', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: CacheUpdateHandler.tsx ~ line 126 ~ downloadFromS3 ~');
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (online) {
      uploadFilesInCache();
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [online]);

  useEffect(() => {
    if (primaryContext.wpFilesFetchStatus === ApiStatus.SUCCESS) {
      if (
        workPackageFiles.length > primaryContext.wpFiles?.length &&
        getValueFromLocalStorage('initialSync') === 'false'
      ) {
        const wpFilesProxyList = primaryContext.wpFiles.map(
          (e: WorkPackageFilesOnline) => e.fileId,
        );
        if (wpFilesProxyList) {
          workPackageFiles.forEach((d: WorkPackageFiles) => {
            const index = wpFilesProxyList.findIndex(e => e === d.fileId);
            if (index === -1) {
              workPackageFilesCollection
                ?.find({
                  selector: {
                    fileId: d.fileId,
                  },
                })
                .remove();
            }
          });
        }
      }
      primaryContext.resetWpFiles();
    }
  }, [
    primaryContext,
    primaryContext.wpFilesFetchStatus,
    workPackageFiles,
    workPackageFilesCollection,
  ]);

  const getSignAndStampUserLevelFromRxDB = async () => {
    if (signAndStampUserLevelCollection) {
      const signAndStampUserLevelRxDocument: SignAndStampUserLevelDocument[] =
        await getSignAndStampUserLevel();
      if (signAndStampUserLevelRxDocument.length > 0) {
        const fileIdArray = await getKeysFromAttachmentStore();
        filesCount.setSignAndStampUserLevelFilesCount(signAndStampUserLevelRxDocument.length);
        signAndStampUserLevelRxDocument.map(async item => {
          if (!fileIdArray?.find(e => e === item.fileUUID) && online) {
            await downloadFromS3(item.originalPath).then(async val => {
              attachmentStore.setItem(item.fileUUID, val).then(async val1 => {
                if (val1) {
                  filesCount.decreaseSignAndStampUserLevelFilesCount();
                }
              });
            });
          } else {
            filesCount.decreaseSignAndStampUserLevelFilesCount();
          }
        });
      }
    }
  };

  const getSignAndStampFilesFromRxDB = async () => {
    if (signAndStampFileCollection) {
      const signAndStampFileRxDocument: SignAndStampFileDocument[] = await getSignAndStampFiles();
      if (signAndStampFileRxDocument.length > 0) {
        const fileIdArray = await getKeysFromAttachmentStore();
        filesCount.setSignAndStampFilesCount(signAndStampFileRxDocument.length);
        signAndStampFileRxDocument.map(async item => {
          if (!fileIdArray?.find(e => e === item.fileUUID) && online) {
            await downloadFromS3(item.originalPath).then(async val => {
              attachmentStore.setItem(item.fileUUID, val).then(async val1 => {
                if (val1) {
                  filesCount.decreaseSignAndStampFilesCount();
                }
              });
            });
          } else {
            filesCount.decreaseSignAndStampFilesCount();
          }
        });
      }
    }
  };

  const getWpDataFromRxdb = async () => {
    const workPackagesDocument = await getWorkPackages();
    if (workPackagesDocument) {
      const defaultVal = JSON.parse(getValueFromLocalStorage('selectedDiscipline') || '{}');
      const newCount = workPackagesDocument.length;
      let oldCount = defaultVal?.selectedDiscipline
        ? defaultVal?.selectedDiscipline.substring(defaultVal?.selectedDiscipline?.indexOf('(') + 1)
        : '(0)';
      oldCount = oldCount.substring(0, oldCount.length - 1);

      if (newCount !== Number(oldCount)) {
        const newValue = {
          selectedDiscipline: `${disciplineMapping(
            defaultVal?.selectedDisciplineId,
          )} (${newCount})`,
          selectedDisciplineId: defaultVal.selectedDisciplineId as number,
        };
        localStorage.setItem('selectedDiscipline', JSON.stringify(newValue));
        SiteDiscipline.setDiscipline(newValue);
      }

      setWorkPackageData(workPackagesDocument);
    }
  };

  const getInspectionListFromRxdb = async () => {
    const inspectionListDocument = await getAllInspectionList();
    if (inspectionListDocument) {
      setInspectionListData(JSON.parse(JSON.stringify(inspectionListDocument)));
    }
  };

  const subscribeToSignAndStampUserLevel = async () => {
    if (signAndStampUserLevelInitialized && signAndStampUserLevelCollection) {
      const signAndStampUserLevelUpdateSub = signAndStampUserLevelCollection
        .find()
        .$.subscribe(() => {
          getSignAndStampUserLevelFromRxDB();
        });
      setSignAndStampUserLevelUpdateSubscription(signAndStampUserLevelUpdateSub);
    }
  };

  const subscribeToSignAndStampFile = async () => {
    if (signAndStampFileInitialized && signAndStampFileCollection) {
      const signAndStampUpdateSub = signAndStampFileCollection.find().$.subscribe(() => {
        getSignAndStampFilesFromRxDB();
      });
      setSignAndStampFileUpdateSubscription(signAndStampUpdateSub);
    }
  };

  const subscribeToWorkPackage = async () => {
    if (workPackagesInitialized && workPackagesCollection) {
      const wpSub = workPackagesCollection.find().$.subscribe(() => {
        getWpDataFromRxdb();
      });
      setWpSubscription(wpSub);
    }
  };

  const subscribeToInspectionList = async () => {
    if (inspectionListInitialized && inspectionListCollection) {
      const inspSub = inspectionListCollection.find().$.subscribe(() => {
        getInspectionListFromRxdb();
      });
      setInspectionListSubscription(inspSub);
    }
  };

  const checkDiff = async () => {
    const siteId = SiteDiscipline.siteId;
    const disciplineId = SiteDiscipline.disciplineId;
    if (siteId && disciplineId) {
      await Promise.all([
        primaryContext.getwpFilesFromMobx(siteId, disciplineId),
        primaryContext.getWorkPackageFromMobx(siteId, disciplineId),
        primaryContext.getInspectionListFromMobx(siteId, disciplineId),
      ]);
    }
  };

  /*
    Workpackagefile, AssetFiles, PreviousReport related code is commented. It's for future use.
  */

  //   useEffect(() => {
  //     let subscription: Subscription;
  //     const getWorkPackageFilesFromRxdb = () => {
  //       if (workPackageFilesCollection && workPackageFilesInitialized) {
  //         const findSelector = {
  //           selector: {
  //             siteId: { $eq: SiteDiscipline.siteId },
  //             disciplineId: { $eq: SiteDiscipline.disciplineId },
  //           },
  //         };
  //         subscription = workPackageFilesCollection?.find(findSelector).$.subscribe(async data => {
  //           if (data.length) {
  //             const fileIdArray = await getKeysFromAttachmentStore();
  //             setWorkPackageFiles(data);
  //             filesCount.setWpFileCount(data.length);
  //             let position = 0;
  //             while (position < data.length) {
  //               const itemsForBatch = data.slice(position, position + BATCH_SIZE);
  //               await Promise.all(
  //                 itemsForBatch.map(async item => {
  //                   if (!fileIdArray?.includes(item.fileId)) {
  //                     await downloadFromS3(
  //                       item.filePath.charAt(0) === '/' ? item.filePath.slice(1) : item.filePath,
  //                     ).then(val => {
  //                       attachmentStore.setItem(item.fileId, val).finally(async () => {
  //                         syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                       });
  //                     });
  //                   } else {
  //                     syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                   }
  //                 }),
  //               );
  //               position += BATCH_SIZE;
  //             }
  //           }
  //         });
  //       }
  //     };

  //     getWorkPackageFilesFromRxdb();

  //     return () => {
  //       subscription?.unsubscribe();
  //     };
  //   }, [
  //     SiteDiscipline.disciplineId,
  //     SiteDiscipline.siteId,
  //     workPackageFilesCollection,
  //     workPackageFilesInitialized,
  //   ]);

  //   useEffect(() => {
  //     let subscription: Subscription;
  //     const getPreviousReportFilesFromRxdb = () => {
  //       if (previousReportFilesCollection && previousReportFilesInitialized) {
  //         const findSelector = {
  //           selector: {
  //             site: SiteDiscipline.siteId,
  //             discipline: SiteDiscipline.disciplineId,
  //           },
  //         };
  //         subscription = previousReportFilesCollection.find(findSelector).$.subscribe(async data => {
  //           if (data.length) {
  //             const fileIdArray = await getKeysFromAttachmentStore();
  //             filesCount.setPreviousReportFilesCount(data.length);
  //             let position = 0;
  //             while (position < data.length) {
  //               const itemsForBatch = data.slice(position, position + BATCH_SIZE);
  //               await Promise.all(
  //                 itemsForBatch.map(async item => {
  //                   if (!fileIdArray?.includes(item.pkAssetInspection)) {
  //                     await downloadFromS3(
  //                       item.originalPath.charAt(0) === '/'
  //                         ? item.originalPath.slice(1)
  //                         : item.originalPath,
  //                     ).then(val => {
  //                       attachmentStore.setItem(item.pkAssetInspection, val).finally(async () => {
  //                         syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                       });
  //                     });
  //                   } else {
  //                     syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                   }
  //                 }),
  //               );
  //               position += BATCH_SIZE;
  //             }
  //           }
  //         });
  //       }
  //     };

  //     getPreviousReportFilesFromRxdb();

  //     return () => {
  //       subscription?.unsubscribe();
  //     };
  //   }, [
  //     SiteDiscipline.disciplineId,
  //     SiteDiscipline.siteId,
  //     previousReportFilesCollection,
  //     previousReportFilesInitialized,
  //   ]);

  //   useEffect(() => {
  //     let subscription: Subscription;

  //     const getAssetFilesFromRxdb = () => {
  //       if (assetDetailsCollection && assetDetailsInitialized) {
  //         const findSelector = {
  //           selector: {
  //             site: SiteDiscipline.siteId,
  //             discipline: SiteDiscipline.disciplineId,
  //           },
  //         };
  //         subscription = assetDetailsCollection.find(findSelector).$.subscribe(async data => {
  //           if (data.length) {
  //             const fileIdArray = await getKeysFromAttachmentStore();
  //             const assetFiles: AssetFileInfo[] = await JSON.parse(JSON.stringify(data))
  //               .map((val: any) => val?.assetFiles)
  //               .reduce((a: any, b: any) => [...a, ...b], []);
  //             if (assetFiles.length > 0) {
  //               if (Number(localStorage.getItem('assetFilesCount')) !== assetFiles.length) {
  //                 localStorage.setItem('assetFilesCount', assetFiles.length.toString());
  //                 filesCount.setAssetFilesCount(assetFiles.length);
  //                 let position = 0;
  //                 while (position < assetFiles.length) {
  //                   const itemsForBatch = assetFiles.slice(position, position + BATCH_SIZE);
  //                   await Promise.all(
  //                     itemsForBatch.map(async item => {
  //                       if (!fileIdArray?.includes(item.fileId.toString())) {
  //                         await downloadFromS3(item.path).then(val => {
  //                           attachmentStore.setItem(item.fileId.toString(), val).finally(async () => {
  //                             syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                           });
  //                         });
  //                       } else {
  //                         syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
  //                       }
  //                     }),
  //                   );
  //                   position += BATCH_SIZE;
  //                 }
  //               }
  //             }
  //           }
  //         });
  //       }
  //     };

  //     getAssetFilesFromRxdb();

  //     return () => {
  //       subscription?.unsubscribe();
  //     };
  //   }, [
  //     SiteDiscipline.disciplineId,
  //     SiteDiscipline.siteId,
  //     assetDetailsCollection,
  //     assetDetailsInitialized,
  //   ]);
  useEffect(() => {
    let subscription: Subscription;
    const getWorkPackageFilesFromRxdb = () => {
      if (workPackageFilesCollection && workPackageFilesInitialized) {
        const findSelector = {
          selector: {
            siteId: { $eq: SiteDiscipline.siteId },
            disciplineId: { $eq: SiteDiscipline.disciplineId },
          },
        };
        subscription = workPackageFilesCollection?.find(findSelector).$.subscribe(async data => {
          if (data.length) {
            setWorkPackageFiles(data);
          }
        });
      }
    };

    getWorkPackageFilesFromRxdb();

    return () => {
      subscription?.unsubscribe();
    };
  }, [
    SiteDiscipline.disciplineId,
    SiteDiscipline.siteId,
    workPackageFilesCollection,
    workPackageFilesInitialized,
  ]);

  useEffect(() => {
    let subscription: Subscription;

    const getInspectionFilesFromRxdb = () => {
      if (inspectionFilesCollection) {
        const findSelector = {
          selector: {
            siteId: SiteDiscipline.siteId,
            disciplineId: SiteDiscipline.disciplineId,
            isDelete: 'False',
          },
        };
        subscription = inspectionFilesCollection?.find(findSelector).$.subscribe(async data => {
          if (data.length > 0) {
            const fileIdArray = await getKeysFromUploadQueue();
            inspectionFilesCount.setInspFileCount(data.length);
            let position = 0;
            const deltaFiles: {
              fileUUId: string;
              dateModified: string;
              isUploadedByUser: boolean;
            }[] = [];
            while (position < data.length) {
              const itemsForBatch = data.slice(position, position + BATCH_SIZE);
              await Promise.all(
                itemsForBatch.map(async (item, index) => {
                  const existingFile = downloadedFiles.find(file => file.fileId === item.fileUUID);
                  const currentRetryCount = existingFile ? existingFile.retryCount : 0;
                  if (
                    !checkIfExist({
                      fileUUID: item.fileUUID,
                      dateModified: item.dateModified,
                    }) &&
                    !fileIdArray?.includes(item.fileUUID)
                  ) {
                    deltaFiles.push({
                      fileUUId: item.fileUUID,
                      dateModified: item.dateModified as string,
                      isUploadedByUser: false,
                    });
                    if (
                      !item.extension.includes('video') &&
                      ((item.sizeMb < 100000000 && !item.extension.includes('image')) ||
                        item.extension.includes('image')) &&
                      online &&
                      (!existingFile || currentRetryCount < MAX_RETRIES)
                    ) {
                      await downloadFromS3(item.originalPath).then(val => {
                        attachmentStore.setItem(item.fileUUID, val).finally(async () => {
                          syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
                          if (currentRetryCount === MAX_RETRIES - 1) {
                            sentryCaptureException(
                              {
                                UUID: item.fileUUID,
                                inspectionId: item.inspectionId,
                                taskId: item.fkAssetInspectionTask,
                                isInspectionFile: true,
                              },
                              '~ file: CacheUpdateHandler.tsx ~ line 561 ~ downloadFromS3 ~ Max download attempts reached',
                            );
                          }
                        });
                      });
                    }
                  } else {
                    syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
                  }
                  if (itemsForBatch.length === index + 1 && deltaFiles.length > 0) {
                    setDeltaFileData(deltaFiles);
                  }
                  inspectionFilesCount.decreaseInspFileCount();
                }),
              );
              position += BATCH_SIZE;
            }
          }
        });
      }
    };

    if (inspectionFilesInitialized) getInspectionFilesFromRxdb();

    return () => {
      subscription?.unsubscribe();
    };
  }, [
    SiteDiscipline.disciplineId,
    SiteDiscipline.siteId,
    inspectionFilesCollection,
    inspectionFilesInitialized,
  ]);

  useEffect(() => {
    localStorage.setItem('downloadedFiles', JSON.stringify(downloadedFiles));
  }, [downloadedFiles]);

  useEffect(() => {
    let subscription: Subscription;

    const getDeficiencyFilesFromRxdb = () => {
      if (deficiencyFilesCollection && deficiencyFilesInitialized) {
        const findSelector = {
          selector: {
            siteId: SiteDiscipline.siteId,
            disciplineId: SiteDiscipline.disciplineId,
            isDelete: 'False',
            isActiveDeficiencyFile: { $ne: true },
          },
        };

        subscription = deficiencyFilesCollection.find(findSelector).$.subscribe(async data => {
          if (data.length > 0) {
            const fileIdArray = await getKeysFromUploadQueue();
            deficiencyFileCount.setDefFileCount(data.length);
            let position = 0;
            const deltaFiles: {
              fileUUId: string;
              dateModified: string;
              isUploadedByUser: boolean;
            }[] = [];
            while (position < data.length) {
              const itemsForBatch = data.slice(position, position + BATCH_SIZE);
              await Promise.all(
                itemsForBatch.map(async (item, index) => {
                  const existingFile = downloadedFiles.find(
                    file => file.fileId === item.fileAimsUUID,
                  );
                  const currentRetryCount = existingFile ? existingFile.retryCount : 0;
                  if (
                    !checkIfExist({
                      fileUUID: item.fileAimsUUID,
                      dateModified: item.dateModified,
                    }) &&
                    !fileIdArray?.includes(item.fileAimsUUID) &&
                    online &&
                    (!existingFile || currentRetryCount < MAX_RETRIES)
                  ) {
                    deltaFiles.push({
                      fileUUId: item.fileAimsUUID,
                      dateModified: item.dateModified as string,
                      isUploadedByUser: false,
                    });
                    await downloadFromS3(item.path).then(async val => {
                      attachmentStore.setItem(item.fileAimsUUID, val).finally(async () => {
                        syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
                        setDownloadedFiles(prev => [
                          ...prev.filter(file => file.fileId !== item.fileAimsUUID),
                          { fileId: item.fileAimsUUID, retryCount: currentRetryCount + 1 },
                        ]);
                        if (currentRetryCount === MAX_RETRIES - 1) {
                          sentryCaptureException(
                            {
                              UUID: item.fileAimsUUID,
                              deficiencyId: item.deficiencyId,
                              taskId: item.assetInspectionTask,
                              isDeficiencyFile: true,
                            },
                            '~ file: CacheUpdateHandler.tsx ~ line 640 ~ downloadFromS3 ~ Max download attempts reached',
                          );
                        }
                      });
                    });
                  } else {
                    syncData.setTotalFileCount(syncData.totalDownloadCount + 1);
                  }
                  if (itemsForBatch.length === index + 1 && deltaFiles.length > 0) {
                    setDeltaFileData(deltaFiles);
                  }
                }),
              );
              position += BATCH_SIZE;
            }
          }
        });
      }
    };
    if (deficiencyFilesInitialized) getDeficiencyFilesFromRxdb();

    return () => {
      subscription?.unsubscribe();
    };
  }, [
    SiteDiscipline.disciplineId,
    SiteDiscipline.siteId,
    deficiencyFilesCollection,
    deficiencyFilesInitialized,
  ]);

  useEffect(() => {
    if (signAndStampUserLevelInitialized) {
      subscribeToSignAndStampUserLevel();
    }
    return () => {
      if (signAndStampUserLevelUpdateSubscription)
        signAndStampUserLevelUpdateSubscription.unsubscribe();
    };
  }, [signAndStampUserLevelInitialized]);

  useEffect(() => {
    if (signAndStampFileInitialized) {
      subscribeToSignAndStampFile();
    }
    return () => {
      if (signAndStampFileUpdateSubscription) signAndStampFileUpdateSubscription.unsubscribe();
    };
  }, [signAndStampFileInitialized]);

  useEffect(() => {
    if (workPackagesInitialized) {
      getWpDataFromRxdb();
      subscribeToWorkPackage();
    }
    return () => {
      if (wpSubscription) wpSubscription.unsubscribe();
    };
  }, [workPackagesInitialized, SiteDiscipline.siteId, SiteDiscipline.disciplineId]);

  useEffect(() => {
    if (inspectionListInitialized) {
      getInspectionListFromRxdb();
      subscribeToInspectionList();
    }
    return () => {
      if (inspectionListSubscription) inspectionListSubscription.unsubscribe();
    };
  }, [inspectionListInitialized, SiteDiscipline.siteId, SiteDiscipline.disciplineId]);

  useEffect(() => {
    if (primaryContext.WorkPackageListFetchStatus === ApiStatus.SUCCESS) {
      const dataFromOnlineCall = primaryContext.WorkPackageList.toJSON();
      const wpProxyList = primaryContext.WorkPackageList.map(
        (e: { workPackageId: string }) => e.workPackageId,
      );
      if (workPackageData.length > primaryContext.WorkPackageList.length) {
        const toBeDeleted: string[] = [];
        workPackageData.forEach((d: WorkPackagesDocument) => {
          const isThere = wpProxyList.find(e => e === d.workPackageId);
          if (!isThere) {
            toBeDeleted.push(d.workPackageId);
          }
        });
        if (workPackagesCollection && toBeDeleted.length) {
          workPackagesCollection.bulkRemove(toBeDeleted);
        }
        localStorage.setItem('wpCount', JSON.stringify(wpProxyList.length));
        SiteDiscipline.setWpCount(wpProxyList.length);
      }

      const checkCount = async () => {
        const workpackageResult = await workPackagesCollection?.findByIds(wpProxyList);
        if (workpackageResult) {
          const dataFromRxDB = wpProxyList.map(item => workpackageResult.get(item));
          for (const index in dataFromRxDB) {
            const indexForMobx = dataFromOnlineCall.findIndex(
              wp => wp.workPackageId === dataFromRxDB[index]?.workPackageId,
            );
            if (indexForMobx !== -1) {
              if (
                workPackagesCollection &&
                dataFromOnlineCall[indexForMobx].noOfInspections !==
                  dataFromRxDB[index]?.noOfInspections
              ) {
                await workPackagesCollection
                  .findOne({
                    selector: {
                      workPackageId: dataFromRxDB[index]?.workPackageId,
                    },
                  })
                  .update({
                    $set: {
                      noOfInspections: dataFromOnlineCall[index].noOfInspections,
                    },
                  });
              }
            }
          }
        }
      };
      checkCount();
      primaryContext.resetWorkPackageList();
    }
  }, [primaryContext.WorkPackageListFetchStatus]);

  useEffect(() => {
    const setData = async (uniqueInspList: string[]) => {
      if (inspectionListData.length > uniqueInspList.length) {
        const toBeDeleted: string[] = [];
        for (const inspection in inspectionListData) {
          const isThere = uniqueInspList.find(
            (e: string) => e === inspectionListData[inspection]?.inspectionId,
          );
          if (!isThere) {
            toBeDeleted.push(inspectionListData[inspection]?.inspectionId);
          }
        }
        if (DEBUG) console.log('tobe deleted = ', toBeDeleted);
        if (inspectionListCollection && toBeDeleted.length) {
          inspectionListCollection.bulkRemove(toBeDeleted);
        }
      }
    };

    if (primaryContext.InspectionListFetchStatus === ApiStatus.SUCCESS) {
      const uniqueInspList = primaryContext.InspectionList.toJSON()?.map(item => item.inspectionId);
      if (DEBUG)
        console.log(
          'uniqueInspList = ',
          uniqueInspList,
          JSON.parse(JSON.stringify(inspectionListData)),
        );
      if (inspectionListData.length > uniqueInspList.length) {
        setData(uniqueInspList);
      }
      primaryContext.resetInspectionList();
    }
  }, [primaryContext.InspectionListFetchStatus]);

  useEffect(() => {
    if (intervalId) clearInterval(intervalId);
    let intervalID: NodeJS.Timeout;
    if (online && !HandleGesture.bulkUpload) {
      intervalID = setInterval(checkDiff, LiveInterval.MINUTES_1);
      setIntervalId(intervalID);
    }
  }, [online, SiteDiscipline.siteId, SiteDiscipline.disciplineId, HandleGesture.bulkUpload]);

  return <div data-cy="CacheUpdateHandler" />;
};

export default observer(CacheUpdateHandler);
